<script setup lang="ts">
import { computed } from 'vue'
import { VImg } from '@hatfieldmedia/hmcore'
import type { EventStatus } from '@/types/event'

const props = withDefaults(
  defineProps<{
    image: string
    date: string
    title: string
    description: string
    location: string
    slug: string
    detailType?: string
    status?: EventStatus
  }>(),
  { status: 'open' },
)

defineEmits<{
  register: [slug: string]
  learnMore: [slug: string]
}>()

const isUpcoming = computed(() => {
  if (!props.date) return true
  return new Date(props.date) > new Date()
})

const isInteractive = computed(() => props.status === 'open' && isUpcoming.value)

function convertDate(dateStr: string): string {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString(undefined, options)
}
</script>

<template>
  <client-only>
    <div v-if="isUpcoming" class="event-card" :class="{ 'event-card--inert': !isInteractive }">
      <div class="event-card-media">
        <VImg class="event-card-img" :src="image" />
      </div>
      <div class="event-card-body">
        <p class="event-date">{{ convertDate(date) }}</p>
        <h3 v-html="title"></h3>
        <p>{{ description }}</p>
        <span class="event-loc">{{ location }}</span>
        <div v-if="detailType" class="event-actions">
          <button v-if="isInteractive" class="event-learn-btn" @click="$emit('learnMore', slug)">
            Learn More <span class="arrow">&rarr;</span>
          </button>
          <span v-else-if="status === 'cancelled'" class="event-badge event-badge--cancelled">Cancelled</span>
          <span v-else-if="status === 'closed'" class="event-badge event-badge--closed">Closed</span>
        </div>
      </div>
    </div>
  </client-only>
</template>

<style scoped>
.event-card {
  background: var(--card);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition:
    transform 0.3s,
    box-shadow 0.3s;
  cursor: pointer;
  user-select: none;
}

.event-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
}

.event-card--inert {
  cursor: default;
  opacity: 0.6;
}

.event-card--inert:hover {
  transform: none;
  box-shadow: none;
}

.event-card-media {
  position: relative;
}

.event-card-img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  filter: brightness(0.72);
  display: block;
}

.event-badge {
  font-family: 'HND', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 9px 22px;
  border-radius: 9999px;
  color: #fff;
  text-align: center;
}

.event-badge--cancelled {
  background: #e53935;
}

.event-badge--closed {
  background: #757575;
}

.event-card-body {
  padding: 28px;
}

.event-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.event-learn-btn {
  background: none;
  border: none;
  color: var(--pink-bright);
  font-family: 'HND', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0;
  cursor: pointer;
  transition: color 0.25s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
}

.event-learn-btn:hover {
  color: var(--white);
}

.arrow {
  transition: transform 0.25s;
}

.event-learn-btn:hover .arrow {
  transform: translateX(3px);
}

.event-register-btn {
  padding: 9px 22px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--pink), var(--purple));
  color: #fff;
  font-family: 'HND', sans-serif;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.event-register-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(186, 50, 139, 0.4);
}

.event-date {
  font-family: 'HND', sans-serif;
  color: var(--text);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 12px;
  font-weight: 600;
  color: var(--pink-bright);
}

h3 {
  font-family: 'HNDC',sans-serif;
  font-size: 1.3rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 12px;
}

p {
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--text);
  margin-bottom: 12px;
}

.event-loc {
  font-family: 'HND', sans-serif;
  font-size: 0.92rem;
  color: var(--text);
  letter-spacing: 0.04em;
  line-height: 1.6;
}

.event-loc::before {
  content: '📍 ';
}

@media (max-width: 768px) {
  .event-card-body {
    padding: 20px;
  }

  h3 {
    font-size: 1.15rem;
  }

  p {
    font-size: 0.88rem;
    margin-bottom: 12px;
  }
}

@media (max-width: 390px) {
  .event-card-img {
    height: 180px;
  }
}
</style>
